import { useQueryClient } from '@tanstack/react-query'
import type { FC, RefObject } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Empty from 'src/components/Empty'
import TaskDetailDialog from 'src/components/GenerationTask/TaskDetailDialog'
import TaskPreviewCard from 'src/components/GenerationTask/TaskPreviewCard'
import { openModelExperimentDialog } from 'src/components/ModelExperiment/ModelExperimentDialog'
import { useMyInfo } from 'src/services/apollo/auth'
import type { TaskSummary } from 'src/services/apollo/generated/graphql'
import {
  usePagedListTaskSummaries,
  usePagedListUserTaskSummaries,
} from 'src/services/apollo/task'
import { readExperimentSessionFromCache } from 'src/services/orpc/modelExperiment'
import { getNodesFromConnection } from 'src/utils/graphql'
import { useQs } from 'src/utils/qs'
import { PLATFORM_ROLES } from 'src/utils/rbac/constants'
import type { BaseCursorPagingContextType } from '../CursorPaging/BaseCursorPaging'
import BaseCursorPaging from '../CursorPaging/BaseCursorPaging'
import LoadingPage from '../LoadingPage'
import NotLogin from '../NotLogin'
import { TaskPreviewContentExtra } from './TaskPreviewContentExtra'

export const TaskList: FC<{
  userId: string
  editing: boolean
  selectedItems: string[]
  toggleSelected: (id: string, task: TaskSummary) => void
  pagingRef: RefObject<BaseCursorPagingContextType | null>
  onDataChange?: (data?: TaskSummary[]) => any
}> = ({
  userId,
  editing,
  selectedItems,
  toggleSelected,
  pagingRef,
  onDataChange,
}) => {
  const { t } = useTranslation(['artwork'])
  const [{ before, after, keyword }, setQs] = useQs()

  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [activeTask, setActiveTask] = useState<TaskSummary>()

  const { data: userInfo, loading } = useMyInfo()

  const queryClient = useQueryClient()

  const onClickTask = useCallback(
    (task?: TaskSummary) => {
      if (!task) return
      // Mirror `getEffectiveTaskStatus` in
      // `GenerationTask/Common/taskDisplay`: only `selecting` is the "user
      // needs to pick" state. `pending` means subs are still generating —
      // clicking a pending card should fall through to the regular
      // TaskDetail path, not pop a modal that would immediately self-close.
      const session = readExperimentSessionFromCache({
        queryClient,
        taskId: task.id,
      })
      if (session?.status === 'selecting') {
        openModelExperimentDialog(task.id)
        return
      }
      setActiveTask(task)
      setIsDetailOpen(true)
    },
    [queryClient],
  )

  if (!userInfo) {
    if (loading) return <LoadingPage />
    return <NotLogin />
  }

  const tapTask = (task?: TaskSummary) => {
    if (!task) return
    if (editing) {
      toggleSelected(task.id, task)
    } else {
      onClickTask(task)
    }
  }

  /**
   * @see https://mewtant-inc.sg.larksuite.com/docx/TLn6d8aIqoqwevxewBqlf6JHghb
   */
  const shouldUseUserTaskSummaries = !keyword

  const dataFetchHook = shouldUseUserTaskSummaries
    ? usePagedListUserTaskSummaries
    : usePagedListTaskSummaries

  const extraArgs = {
    userId,
    ...(shouldUseUserTaskSummaries ? {} : { keyword }), // keyword is only applicable to useListTaskSummaries
  }

  return (
    <>
      {userInfo.id === userId ||
      userInfo.platformRoles?.includes(PLATFORM_ROLES.SUPER_ADMIN) ? (
        <BaseCursorPaging
          useSWR={dataFetchHook}
          getConnection={r => r}
          pageSize={20}
          renderItem={(task: TaskSummary) =>
            userInfo.id === userId ? (
              <TaskPreviewCard
                isEdit={editing}
                selected={selectedItems.includes(task.id)}
                key={task.id}
                taskSummary={task}
                onTaskClick={tapTask}
                contentExtra={<TaskPreviewContentExtra taskSummary={task} />}
              />
            ) : (
              // Admin view: edit/select is disabled
              <TaskPreviewCard
                key={task.id}
                taskSummary={task}
                onTaskClick={onClickTask}
                contentExtra={<TaskPreviewContentExtra taskSummary={task} />}
              />
            )
          }
          extraArgs={extraArgs}
          empty={<Empty description={t('artwork:generation.no-task')} />}
          before={before}
          after={after}
          onCursorChange={setQs}
          pagingRef={pagingRef}
          onDataChange={data => {
            const tasks = getNodesFromConnection(data)
            onDataChange && tasks.length > 0 ? onDataChange(tasks) : null
          }}
        />
      ) : null}

      <TaskDetailDialog
        task={activeTask?.id}
        taskSummary={activeTask}
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />
    </>
  )
}
